import type { OfficeActivity, OfficeAgent, OfficeMessage, OfficeTask } from "@foreman/protocol";

/** The team on a computer, as the phone last heard it (everything in `office.state` but the message tag). */
export interface OfficeSnapshot {
  available: boolean;
  reason?: string;
  agents: OfficeAgent[];
  tasks: OfficeTask[];
  windowVisible: boolean | null;
  at: number;
  /** Whether this computer can run a team and where that stands (absent on computers that predate it). */
  engine?: "unavailable" | "stopped" | "starting" | "running" | "failed";
  engineMessage?: string;
}

/** Newest first, per agent. */
export type ActivityBuffer = Record<string, OfficeActivity[]>;

/** Most recent things kept per agent on the phone; older ones are fetched a page at a time. */
export const ACTIVITY_CAP = 200;

/** Add one live entry. Same id twice (a reconnect replaying it) is kept once; order stays newest first. */
export function addActivity(buf: ActivityBuffer, entry: OfficeActivity): ActivityBuffer {
  const list = buf[entry.agentId] ?? [];
  if (list.some((e) => e.id === entry.id)) return buf;
  const next = [entry, ...list].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)).slice(0, ACTIVITY_CAP);
  return { ...buf, [entry.agentId]: next };
}

/** Merge a page fetched from the computer into what is already held, deduplicated, newest first. */
export function mergeActivity(held: OfficeActivity[], page: OfficeActivity[]): OfficeActivity[] {
  const seen = new Set<string>();
  const out: OfficeActivity[] = [];
  for (const e of [...held, ...page]) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
  }
  return out.sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

export type AgentStatus = "working" | "needs-you" | "idle" | "paused" | "stopped" | "slowed";

/** How recently an agent must have acted to count as working. */
export const WORKING_WITHIN_SEC = 20;

/**
 * What to say about an agent right now, from what the computer reports. `latest` is its newest
 * activity (live or fetched); it is the only way to know it is waiting on the person.
 */
export function agentStatus(agent: OfficeAgent, latest?: OfficeActivity): { status: AgentStatus; label: string } {
  if (agent.breaker === "stopped") return { status: "stopped", label: "Stopped" };
  if (agent.onHold) return { status: "paused", label: "Paused" };
  if (latest?.kind === "waiting" && (agent.lastActiveSecAgo === null || agent.lastActiveSecAgo < 600)) return { status: "needs-you", label: "Needs you" };
  if (agent.breaker !== "healthy") return { status: "slowed", label: "Slowed down" };
  if (agent.lastActiveSecAgo !== null && agent.lastActiveSecAgo < WORKING_WITHIN_SEC) return { status: "working", label: "Working" };
  return { status: "idle", label: "Idle" };
}

/** "3 of 5 steps done", for the plan header. */
export function planProgress(tasks: OfficeTask[]): { done: number; total: number; active: number; blocked: number } {
  let done = 0;
  let active = 0;
  let blocked = 0;
  for (const t of tasks) {
    if (t.status === "done") done++;
    else if (t.status === "doing") active++;
    else if (t.status === "blocked") blocked++;
  }
  return { done, total: tasks.length, active, blocked };
}

/** Tasks for one agent, active ones first. */
export function tasksFor(tasks: OfficeTask[], agentId: string): OfficeTask[] {
  const rank = (s: string) => (s === "doing" ? 0 : s === "blocked" ? 1 : s === "todo" ? 2 : 3);
  return tasks.filter((t) => t.assignee === agentId).sort((a, b) => rank(a.status) - rank(b.status));
}

/** Group a newest-first activity list by calendar day for the History timeline ("Today", "Yesterday", "Oct 14"). */
export function groupByDay(entries: OfficeActivity[], now = Date.now()): { day: string; entries: OfficeActivity[] }[] {
  const startOfDay = (t: number) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const today = startOfDay(now);
  const groups: { day: string; entries: OfficeActivity[] }[] = [];
  for (const e of entries) {
    const d = startOfDay(e.ts);
    const label = d === today ? "Today" : d === today - 86_400_000 ? "Yesterday" : new Date(d).toLocaleDateString("en", { month: "short", day: "numeric" });
    const last = groups[groups.length - 1];
    if (last && last.day === label) last.entries.push(e);
    else groups.push({ day: label, entries: [e] });
  }
  return groups;
}

/** Newest first, whole team. */
export const MESSAGE_CAP = 100;

/** Add a message, once: the same id again (a replay on reconnect, or the history fetch overlapping the live stream) is ignored. */
export function addMessage(list: OfficeMessage[], m: OfficeMessage): OfficeMessage[] {
  if (list.some((x) => x.id === m.id)) return list;
  return [...list, m].sort((a, b) => b.ts - a.ts || (a.id < b.id ? 1 : -1)).slice(0, MESSAGE_CAP);
}

export function mergeMessages(held: OfficeMessage[], page: OfficeMessage[]): OfficeMessage[] {
  return page.reduce((acc, m) => addMessage(acc, m), held);
}

export type ChatItem =
  | { kind: "you"; id: string; ts: number; text: string }
  | { kind: "dot"; id: string; ts: number; text: string }
  | { kind: "handover"; id: string; ts: number; from: string; to: string; message: OfficeMessage };

/**
 * The conversation with the orchestrator, oldest first: what the person said, what the orchestrator answered, and each step
 * handed from one agent to another. `leadId` is the orchestrator; a message to it from an agent is a hand-over (a report), a
 * message from it to an agent is a hand-over (an assignment), and anything to "human" from it is its answer.
 */
export function chatFrom(messages: OfficeMessage[], leadId: string | undefined): ChatItem[] {
  const lead = leadId ?? "god";
  const out: ChatItem[] = [];
  for (const m of [...messages].sort((a, b) => a.ts - b.ts || (a.id < b.id ? -1 : 1))) {
    if (m.from === "human") {
      // The reply footer the phone adds to a goal is for the orchestrator, not for the person reading their own message.
      const text = m.body.split("\n\n(Sent from the human")[0]!.trim() || m.subject;
      out.push({ kind: "you", id: m.id, ts: m.ts, text });
    } else if (m.to === "human") {
      out.push({ kind: "dot", id: m.id, ts: m.ts, text: m.body.trim() || m.subject });
    } else if (m.from !== m.to) {
      out.push({ kind: "handover", id: m.id, ts: m.ts, from: m.from === "god" ? lead : m.from, to: m.to === "god" ? lead : m.to, message: m });
    }
  }
  return out;
}
